import React, { useState, useEffect } from "react";
import {
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import { format } from "date-fns";
import { retrieveTravelCalendarEntriesByUserIdApi } from "../../../components/api/TravelCalendarApiService";

// Compute annual leave day counts from a list of entries.
// Groups by date so a day with FIRST + LAST half-entries is counted as 1 full day, not 2.
const computeAnnualLeaveCounts = (allEntries) => {
  const sumByDate = (entries) => {
    const byDate = {};
    entries.forEach((e) => {
      const dateKey = e.entryDate ? String(e.entryDate).substring(0, 10) : "unknown";
      const weight = e.halfDayType === "FIRST" || e.halfDayType === "LAST" ? 0.5 : 1.0;
      byDate[dateKey] = Math.min(1.0, (byDate[dateKey] || 0) + weight);
    });
    return Object.values(byDate).reduce((sum, w) => sum + w, 0);
  };

  return {
    actualDays: sumByDate(allEntries.filter((e) => e.category === "ANNUAL_LEAVE" && e.entryType === "ACTUAL")),
    approvedDays: sumByDate(allEntries.filter((e) => e.category === "ANNUAL_LEAVE" && e.approvalStatus === "APPROVED")),
  };
};

const PendingApprovalPanel = ({ pendingEntries, onNavigate, onApprove }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  // Map of userId → { actualDays, approvedDays }
  const [leaveCounts, setLeaveCounts] = useState({});

  const count = pendingEntries?.length ?? 0;

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  // When the panel opens, fetch all entries for users who have ANNUAL_LEAVE pending entries
  useEffect(() => {
    if (!open) return;
    const annualLeaveUserIds = [
      ...new Set(
        (pendingEntries || [])
          .filter((e) => e.category === "ANNUAL_LEAVE" && e.user?.id)
          .map((e) => e.user.id)
      ),
    ];
    annualLeaveUserIds.forEach((uid) => {
      if (leaveCounts[uid]) return; // already fetched
      retrieveTravelCalendarEntriesByUserIdApi(uid)
        .then((res) => setLeaveCounts((prev) => ({ ...prev, [uid]: computeAnnualLeaveCounts(res.data || []) })))
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingEntries]);

  return (
    <>
      <Tooltip title={count > 0 ? `${count} pending approval${count !== 1 ? "s" : ""}` : "No pending approvals"}>
        <span>
          <IconButton
            size="small"
            onClick={handleOpen}
            sx={{
              color: count > 0 ? "#e65100" : "text.secondary",
            }}
          >
            <Badge badgeContent={count} color="warning" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { width: 360, maxHeight: 420, display: "flex", flexDirection: "column", borderRadius: 2 },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, background: "#fff8e1", borderBottom: "1px solid #ffe082" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#e65100" }}>
            Pending Approvals{count > 0 ? ` (${count})` : ""}
          </Typography>
        </Box>

        {count === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No entries waiting for approval.
            </Typography>
          </Box>
        ) : (
          <List disablePadding dense sx={{ overflowY: "auto" }}>
            {pendingEntries.map((entry, index) => {
              const userName = entry.user?.name || entry.user?.username || `User #${entry.user?.id}`;
              const dateStr = entry.entryDate
                ? format(new Date(entry.entryDate), "dd MMM yyyy")
                : "—";
              const destination =
                entry.flights && entry.flights.length > 0
                  ? entry.flights.map((f) => f.destination).filter(Boolean).join(" → ") || "—"
                  : entry.destination || "—";
              const category = entry.category || "";
              const isAnnualLeave = category === "ANNUAL_LEAVE";
              const userLeave = isAnnualLeave ? leaveCounts[entry.user?.id] : null;
              const fmtDays = (n) => (n % 1 === 0 ? String(n) : Number(n).toFixed(1));

              return (
                <React.Fragment key={entry.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Tooltip title="Approve">
                        <IconButton
                          edge="end"
                          size="small"
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            onApprove(entry.id);
                          }}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemButton
                      onClick={() => {
                        handleClose();
                        onNavigate(entry);
                      }}
                      sx={{ pr: 6 }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                            <Typography variant="body2" fontWeight="bold" component="span">
                              {userName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="span">·</Typography>
                            <Typography variant="body2" component="span">{dateStr}</Typography>
                            {category && (
                              <Chip label={category} size="small" sx={{ fontSize: "0.6rem", height: 16 }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                            <Typography variant="caption" color="text.secondary" noWrap component="span">
                              {destination}
                            </Typography>
                            {isAnnualLeave && userLeave && (
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  backgroundColor: "#e3f2fd",
                                  borderRadius: 1,
                                  px: 0.8,
                                  py: 0.2,
                                  mt: 0.3,
                                  width: "fit-content",
                                }}
                              >
                                <BeachAccessIcon sx={{ fontSize: 11, color: "#1565c0" }} />
                                <Typography variant="caption" sx={{ color: "#1565c0", fontWeight: 600, fontSize: "0.62rem" }} component="span">
                                  {fmtDays(userLeave.actualDays)} actual · {fmtDays(userLeave.approvedDays)} approved days
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
};

export default PendingApprovalPanel;
